import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../../foundation/altzero_base/base/components/ui/label';
import {
    Popover, PopoverContent, PopoverTrigger,
} from '../../../../foundation/altzero_base/base/components/ui/popover';
import { Plus, X, Wand2, AlertTriangle, Calendar, ChevronDown } from 'lucide-react';
import {
    normaliseTermSeries, offerTermsOutsideSeries, seriesYears, classifyOutsideTerm,
    type TenderTermSeries,
} from '../../lib/tenderTermSeries';
import { proposeOfferReterm, type TermTarget } from '../../lib/bulkReterm';

/**
 * The tender's contract-term series, stated once for the whole tender.
 *
 * ⚠️ WHY THIS SCREEN EXISTS.
 *
 * `sys_config.requested_contract_terms` is one ORG-WIDE row, `[12,24,36,48,60,72]`. A tender
 * that asked retailers for 4/16/28/40 months had nowhere to say so, so every term was labelled
 * by snapping to that global list — 4 and 16 both snapped to the 12-month bucket and collided
 * on Year 1. The generated document then keyed its "Year 1" page to term 12 and printed AGL's
 * 16-MONTH rates on it, showed the 40-month figures on BOTH the Year 3 and Year 4 pages, and
 * dropped AGL's and Origin's real 4-month quotes entirely. Silently, every time.
 *
 * Stating the series here replaces 28 per-offer dropdowns (7 offers x 4 terms) with four
 * inputs, and makes the answer explicit instead of inferred. It is also the shape the client's
 * own workbook uses — their Data Input Page ends with an index -> months table:
 *
 *   1 -> 4 months    2 -> 16 months    3 -> 28 months    4 -> 40 months
 *
 * A per-offer assignment still overrides the series, for the retailer who answers a 16-month
 * request with 15.
 *
 * ⚠️ IT IS A POPOVER, NOT A PANEL, AND THE TRIGGER CARRIES THE STATE.
 *
 * Stage 3 is a three-column review screen in a fixed-height flex column — every row of chrome
 * above it comes straight out of the offer list and the rate table below. As a permanent block
 * this cost ~160px of that height to four inputs touched once per tender. Collapsed to a single
 * chip it costs ~32px, so the summary, the "not set" state and the mismatch count all have to
 * live ON the chip: a control that hides its own warnings is worse than one that takes space.
 */

/** One of an offer's terms, with the year the series suggests for it. */
export type RetermTermRow = {
    fromKey: string;
    fromMonths: number;
    /** `_smooth` / `_stepped`, kept on the new key. */
    suffix: string;
    suggested: TermTarget;
    /** The term already IS that year's length — listed, but nothing to do. */
    alreadyCorrect: boolean;
};

/** One offer's full ladder against this tender's series. */
export type RetermOfferRow = {
    offerId: string;
    retailer: string;
    terms: RetermTermRow[];
    /** True when at least one term would actually move — the row opens ticked. */
    suggestSelected: boolean;
};

/** What the reviewer chose, for the offers they ticked. */
export type RetermSelection = {
    offerId: string;
    terms: { fromKey: string; fromMonths: number; suffix: string; target: TermTarget }[];
};

type TenderTermSeriesPanelProps = {
    series: TenderTermSeries;
    onChange: (series: TenderTermSeries) => void;
    /** The series this tender's offers imply, offered as a seed. */
    proposed: TenderTermSeries;
    /** Retailer name -> the terms that offer quotes, for the mismatch warnings. */
    offerTerms: { offerId?: string; retailer: string; terms: number[] }[];
    /** Move one term of one offer onto a year's month count. */
    onRetermTerm?: (offerId: string, fromMonths: number, toMonths: number) => void;
    /** Contract start, so each year can show the span it will be priced over. */
    contractStart?: string | null;
    /** What each retailer quoted as its own first day, for the disagreement warning. */
    offerStarts?: { retailer: string; start: string }[];
    /** True when those dates are not all the same — see the note on startsWarning. */
    startsDisagree?: boolean;
    /** Every offer's ladder with a per-term suggestion, all of them changeable. */
    retermOffers?: RetermOfferRow[];
    onApplyReterm?: (selections: RetermSelection[]) => void | Promise<void>;
    disabled?: boolean;
};

const MS_PER_DAY = 86_400_000;

/** Calendar-month arithmetic that cannot roll past the end of the target month. */
const addMonths = (date: Date, months: number): Date => {
    const day = date.getUTCDate();
    const first = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
    const daysInTarget = new Date(Date.UTC(
        first.getUTCFullYear(), first.getUTCMonth() + 1, 0,
    )).getUTCDate();
    first.setUTCDate(Math.min(day, daysInTarget));
    return first;
};

/** 29 Februaries in (start, end]. Excluded from the span, matching the engine. */
const countLeapDays = (start: Date, end: Date): number => {
    let count = 0;
    for (let y = start.getUTCFullYear(); y <= end.getUTCFullYear(); y++) {
        if (y % 4 !== 0 || (y % 100 === 0 && y % 400 !== 0)) continue;
        const feb29 = Date.UTC(y, 1, 29);
        if (feb29 > start.getTime() && feb29 <= end.getTime()) count++;
    }
    return count;
};

/**
 * The span a year's month count implies, in leap-excluded days, and the multiplier it produces.
 *
 * Shown because every dollar on that term is an ANNUAL figure times this number, and it was
 * invisible. The client's model priced WERRIBEE's 16-month term over 485 days where the dates
 * give 486 — a 0.21% gap on every retailer that nobody could see, let alone check.
 */
const spanFor = (contractStart: string | null | undefined, months: number) => {
    if (!contractStart || !(months > 0)) return null;
    const ms = Date.parse(`${contractStart.slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(ms)) return null;
    const start = new Date(ms);
    const end = new Date(addMonths(start, months).getTime() - MS_PER_DAY);
    const days = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) - countLeapDays(start, end);
    if (!Number.isFinite(days) || days <= 0) return null;
    return { days, weight: days / 365, end };
};

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** A target as one string — the value of a `<select>`, and how a proposal is fingerprinted. */
const targetKey = (target: TermTarget): string =>
    target.kind === 'year' ? `year:${target.year}` : target.kind;

/** Same months in the same years — used to skip a pointless save on close. */
const sameSeries = (a: TenderTermSeries, b: TenderTermSeries): boolean => {
    const x = normaliseTermSeries(a);
    const y = normaliseTermSeries(b);
    if (x.size !== y.size) return false;
    for (const [year, months] of x) if (y.get(year) !== months) return false;
    return true;
};

export const TenderTermSeriesPanel: React.FC<TenderTermSeriesPanelProps> = ({
    series, onChange, proposed, offerTerms, contractStart,
    offerStarts = [], startsDisagree = false,
    retermOffers: retermOffersProp = [], onApplyReterm, onRetermTerm, disabled = false,
}) => {
    const [open, setOpen] = useState(false);
    const [retermBusy, setRetermBusy] = useState(false);

    /**
     * The popover edits a DRAFT and commits once, on close.
     *
     * Editing `series` directly would fire the tender-level save on every keystroke — typing
     * "16" into an empty Year 2 writes "1" then "16", and each write is a round trip that also
     * republishes the label mapping to every open screen. The draft keeps the popover
     * responsive and makes one save per visit.
     */
    const [draft, setDraft] = useState<TenderTermSeries>(series);

    // Re-seed whenever the popover opens, and whenever a series arrives from elsewhere (the
    // tender finishing its load) while the popover is shut.
    useEffect(() => { if (!open) setDraft(series); }, [series, open]);

    /**
     * Save now, not when the popover shuts.
     *
     * ⚠️ ADDING OR DELETING A YEAR IS ONE ACT, AND IT USED TO SIT UNSAVED.
     *
     * Only the "use suggested" buttons committed; the X and + wrote to the draft and left the
     * tender on its old series. The chip above then read 3 · 15 · 27 · 39 · 51 while the editor
     * below it showed 15 · 27 · 39 · 51, every warning and every page count still answered to
     * the series with the 3 in it, and a reviewer who deleted a year and pressed Apply rather
     * than closing the popover had changed nothing at all.
     *
     * Typing a MONTH COUNT still defers — "16" arrives as "1" then "16" and each save is a round
     * trip that republishes the label mapping to every open screen. One keystroke is not a
     * decision; adding or removing a year is.
     */
    const commit = (next: TenderTermSeries) => {
        setDraft(next);
        if (!sameSeries(next, series)) onChange(next);
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && !sameSeries(draft, series)) onChange(draft);
        setOpen(nextOpen);
    };

    const years = useMemo(() => seriesYears(draft), [draft]);
    const proposedYears = useMemo(() => seriesYears(proposed), [proposed]);
    const normalised = useMemo(() => normaliseTermSeries(draft), [draft]);

    /**
     * Every offer re-proposed against the series IN THE EDITOR, not the one last saved.
     *
     * ⚠️ ONE PANEL WAS RUNNING ON TWO SERIES AND IT SHOWED.
     *
     * `years` and `normalised` above — the dropdown's options and their month labels — come from
     * `draft`, what the reviewer is looking at. The proposals arrived as a prop, computed by the
     * parent against the series last SAVED. While those differ every row is drawn against one
     * series and judged against the other:
     *
     *   - a target of Year 5 has no Year 5 option in a four-year draft, so the browser falls back
     *     to showing the FIRST option — Origin's 51-month term read "Year 1 · 15mo"
     *   - "already correct" was decided against the saved series and printed beside a label built
     *     from the draft, so a 3-month term read "Year 1 · 15mo — already correct"
     *
     * Re-proposing here costs nothing (the term keys are already in hand) and leaves the section
     * internally consistent: options, labels, suggestions, clash detection and Apply all answer
     * to the same series. `mismatches` above deliberately stays on the saved series — the chip
     * must not flicker while someone is typing a year.
     */
    const retermOffers = useMemo(() => retermOffersProp.map(offer => {
        const proposal = proposeOfferReterm(offer.terms.map(t => t.fromKey), draft);
        return {
            ...offer,
            terms: proposal.terms.map(t => ({
                fromKey: t.fromKey,
                fromMonths: t.fromMonths,
                suffix: t.suffix,
                suggested: t.suggested,
                alreadyCorrect: t.alreadyCorrect,
            })),
            suggestSelected: proposal.suggestSelected,
        };
    }), [retermOffersProp, draft]);
    const isEmpty = normaliseTermSeries(series).size === 0;

    /**
     * Warnings are computed against the SAVED series — the chip must not flicker mid-typing.
     *
     * Each leftover term is classified, because "longer than the tender asked for" and "sits in
     * a gap between two years" need opposite responses: the first is information, the second is
     * a missing year somebody has to decide about.
     */
    const mismatches = useMemo(() => offerTerms
        .map(o => ({
            offerId: o.offerId,
            retailer: o.retailer,
            outside: offerTermsOutsideSeries(o.terms, series)
                .map(months => ({ months, placement: classifyOutsideTerm(months, series) })),
        }))
        .filter(o => o.outside.length > 0), [offerTerms, series]);

    /** Offers the re-term list already covers — listing them again below is noise. */
    const plannedRetailers = useMemo(
        () => new Set(retermOffers.filter(o => o.suggestSelected).map(o => o.retailer)),
        [retermOffers]);

    /**
     * The reviewer's choices — one per contract LENGTH, seeded from the suggestion.
     *
     * ⚠️ KEYED ON MONTHS, NOT ON THE TERM KEY.
     *
     * `_smooth` and `_stepped` are two rate variants of one contract length, and Energy
     * Australia sends both: 12, 12_stepped, 24, 24_stepped, 36, 36_stepped, 46, 46_stepped.
     * Per-key choices made that eight independent decisions — eight rows to set, and a smooth
     * ladder that could be re-termed while its own stepped ladder stayed behind, pricing one
     * offer over two different contract lengths. One decision per length, fanned out to every
     * variant of it on apply.
     *
     * ⚠️ KEYED ON THE OFFERS' OWN LENGTHS, NEVER ON THE SUGGESTIONS.
     *
     * The suggestions move with the series being edited, so including them here wiped every
     * decision on the way to typing a year: "16" arrives as "1" then "16", each keystroke a new
     * proposal, each one clearing targets and unticking every offer. The lengths only change
     * when an offer is re-parsed or removed, which is the one case a half-made decision really
     * is stale.
     *
     * A target that names a year the series no longer has is dropped on its own, by the effect
     * below — precisely, without discarding the rest.
     */
    const proposalKey = useMemo(() => retermOffers
        .map(o => `${o.offerId}:${o.terms.map(t => t.fromKey).join(',')}`)
        .join('|'), [retermOffers]);

    /** One row per contract length, in ascending order, for each offer. */
    const lengthsByOffer = useMemo(() => {
        const out = new Map<string, RetermTermRow[]>();
        for (const offer of retermOffers) {
            const seen = new Map<number, RetermTermRow>();
            for (const t of offer.terms) if (!seen.has(t.fromMonths)) seen.set(t.fromMonths, t);
            out.set(offer.offerId, [...seen.values()].sort((a, b) => a.fromMonths - b.fromMonths));
        }
        return out;
    }, [retermOffers]);

    const [targets, setTargets] = useState<Record<string, Record<number, TermTarget>>>({});
    const [picked, setPicked] = useState<Set<string>>(new Set());
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    /**
     * `targets` holds ONLY the rows someone has deliberately set.
     *
     * It used to be pre-filled with every row's suggestion, which meant targetFor never reached
     * its fallback and a row could not follow the suggestion as the series changed underneath
     * it. Left empty, an untouched row tracks the live proposal and a touched one stays put —
     * which is what both of them should do.
     */
    useEffect(() => {
        setTargets({});
        setPicked(new Set(retermOffers.filter(o => o.suggestSelected).map(o => o.offerId)));
        setExpanded(new Set());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proposalKey]);

    /**
     * Drop a decision that names a year the series no longer has.
     *
     * Deleting Year 4 while a term is pointed at it would otherwise leave a target applied to
     * nothing — and, because the year is gone from the options, a select showing the first one
     * instead. Only those entries go; every other decision survives the edit.
     */
    useEffect(() => {
        setTargets(prev => {
            let changed = false;
            const next: Record<string, Record<number, TermTarget>> = {};
            for (const [offerId, byMonths] of Object.entries(prev)) {
                const kept: Record<number, TermTarget> = {};
                for (const [months, target] of Object.entries(byMonths)) {
                    if (target.kind === 'year' && !years.includes(target.year)) { changed = true; continue; }
                    kept[Number(months)] = target;
                }
                next[offerId] = kept;
            }
            return changed ? next : prev;
        });
    }, [years]);

    const targetFor = (offerId: string, row: RetermTermRow): TermTarget =>
        targets[offerId]?.[row.fromMonths] ?? row.suggested;

    /**
     * ⚠️ CHOOSING A YEAR ALSO TICKS THE OFFER. IT DID NOT, AND THE PANEL LOOKED BROKEN.
     *
     * `picked` starts as the offers the panel itself suggested, and Apply only touches those.
     * Setting a year by hand wrote to `targets` alone, so a reviewer who opened an offer the
     * panel had not suggested, moved its 3-month term onto Year 1, and pressed Apply changed
     * nothing — the button read "Apply to 0" and was disabled, which reads as a dead control
     * rather than as "you still have to tick the row".
     *
     * Deliberately touching a term is the clearest statement that this offer is in scope, so it
     * selects it. Apply is still an explicit press, and the checkbox still unticks it.
     */
    const setTarget = (offerId: string, months: number, target: TermTarget) => {
        setTargets(prev => ({ ...prev, [offerId]: { ...(prev[offerId] ?? {}), [months]: target } }));
        setPicked(prev => (prev.has(offerId) ? prev : new Set(prev).add(offerId)));
    };

    const toggle = (set: Set<string>, id: string) => {
        const next = new Set(set);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    };

    /**
     * Two different lengths of one offer aimed at the same year.
     *
     * movesFromTargets lets the first win, which silently drops the second — so the clash is
     * shown and Apply refuses rather than quietly pricing one term less. Variants cannot clash:
     * they share a single decision.
     */
    const clashingOffers = useMemo(() => {
        const out = new Set<string>();
        for (const offer of retermOffers) {
            const claimed = new Set<number>();
            for (const row of lengthsByOffer.get(offer.offerId) ?? []) {
                const target = targetFor(offer.offerId, row);
                if (target.kind !== 'year') continue;
                if (claimed.has(target.year)) out.add(offer.offerId);
                claimed.add(target.year);
            }
        }
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [retermOffers, lengthsByOffer, targets]);

    /**
     * Which of this offer's lengths currently claims each year.
     *
     * ⚠️ TWO LENGTHS CANNOT BE THE SAME YEAR, AND THE PANEL USED TO LET YOU ASK FOR IT.
     *
     * Engie answered PRINCE FABRICATIONS with 3 / 15 / 27 / 51 against a 15/27/39/51 series.
     * Its 15 is already Year 1, so pointing the 3-month stub at Year 1 too asks for one year to
     * hold two terms. movesFromTargets refuses that — it claims a year for the first length that
     * asks and skips every later one — so Apply ran and changed nothing, with no message. Worse
     * the other way round: had the stub been seen first it would have claimed Year 1 and the
     * move would have overwritten the real 15-month term with three months of rates.
     *
     * So the clash is shown where it is made, and the option cannot be picked.
     */
    const yearClaims = (offerId: string): Map<number, number> => {
        const claims = new Map<number, number>();
        for (const row of lengthsByOffer.get(offerId) ?? []) {
            const t = targetFor(offerId, row);
            if (t.kind === 'year' && !claims.has(t.year)) claims.set(t.year, row.fromMonths);
        }
        return claims;
    };

    /** What each offer's terms would become — every variant carrying its length's decision. */
    const selectionFor = (offer: RetermOfferRow): RetermSelection => ({
        offerId: offer.offerId,
        terms: offer.terms.map(t => ({
            fromKey: t.fromKey,
            fromMonths: t.fromMonths,
            suffix: t.suffix,
            target: targets[offer.offerId]?.[t.fromMonths] ?? t.suggested,
        })),
    });

    /** The lengths of one offer that would actually move or be dropped. */
    const changingRows = (offer: RetermOfferRow) =>
        (lengthsByOffer.get(offer.offerId) ?? []).filter(row => {
            const target = targetFor(offer.offerId, row);
            if (target.kind === 'remove') return true;
            return target.kind === 'year' && target.months !== row.fromMonths;
        });

    /** Offers that are ticked AND would actually change something. */
    /**
     * ⚠️ A CLASHING OFFER IS STILL SENT, SO SOMETHING CAN SAY WHAT HAPPENED TO IT.
     *
     * It used to be filtered out here, which meant Apply reported "nothing to re-term" for an
     * offer the reviewer had just aimed somewhere — the red note above was the only sign, and it
     * says what is wrong, not that the press did nothing. applyOfferReterm already refuses to
     * overwrite a length the offer holds, so sending it is safe: the moves it can make are made
     * and the ones it cannot are named in the toast.
     */
    const applicable = useMemo(() => retermOffers.filter(offer =>
        picked.has(offer.offerId)
        && changingRows(offer).length > 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [retermOffers, picked, targets, lengthsByOffer]);

    /**
     * Terms in a gap that NOTHING else is offering to fix.
     *
     * ⚠️ AN OFFER MUST APPEAR IN ONE PLACE, NOT TWO.
     *
     * ALINTA's 12/24/36 are each "between two years", so they landed in this list as well as in
     * the re-term list above — the same three offers stated twice, with the badge counting nine
     * terms that one button already handles. Anything covered by a plan belongs to that plan.
     */
    const gaps = useMemo(() => mismatches
        .filter(m => !plannedRetailers.has(m.retailer))
        .map(m => ({ ...m, outside: m.outside.filter(o => o.placement?.kind !== 'beyond') }))
        .filter(m => m.outside.length > 0), [mismatches, plannedRetailers]);

    /** Terms longer than the tender's last year — extra options, nothing to fix. */
    const extras = useMemo(() => mismatches
        .filter(m => !plannedRetailers.has(m.retailer))
        .map(m => ({ ...m, outside: m.outside.filter(o => o.placement?.kind === 'beyond') }))
        .filter(m => m.outside.length > 0), [mismatches, plannedRetailers]);

    /** Two years sharing a month count would make two pages of the same data. */
    const duplicateMonths = useMemo(() => {
        const seen = new Map<number, number>();
        const dupes: number[] = [];
        for (const [, months] of normalised) seen.set(months, (seen.get(months) ?? 0) + 1);
        for (const [months, count] of seen) if (count > 1) dupes.push(months);
        return dupes;
    }, [normalised]);

    /**
     * Offers whose ladder this series does not describe, and which the list below would move.
     *
     * ⚠️ THE DEDUPE BELOW DELETED THE ONLY SIGNAL ON THE CHIP.
     *
     * `gaps` deliberately drops every offer the re-term list already covers, so the same offer
     * is not stated twice — and the chip's badge counts `gaps`. When EVERY mismatching offer is
     * coverable, that count is zero. A WERRIBEE-shaped tender asking 4/16/28/40 whose ALINTA,
     * Momentum and Engie all answered 12/24/36 therefore showed a chip reading
     * "Contract terms 4 · 16 · 28 · 40 mo" with nothing on it at all: no gap badge (all three
     * were covered), no proposal badge (the series IS what most offers quote), and the re-term
     * list itself living inside a closed popover.
     *
     * All three were then blank on every page of the generated document — the server logs
     * "quotes 12/24/36 months, which the tender's term series does not describe", to a console
     * the reviewer never sees — and the report shipped with three of seven retailers missing.
     *
     * `suggestSelected` is exactly the right predicate: true only when a term would actually
     * move. AGL quoting the tender's own 4/16/28/40 is false, and so is Flo's 16/28/40/52,
     * whose 52 is suggested as Keep. Neither is badged.
     */
    const needReterm = useMemo(
        () => retermOffers.filter(o => o.suggestSelected), [retermOffers]);

    /** `4 · 16 · 28 · 40` — the whole series on the chip, in year order. */
    const summary = useMemo(() => {
        const saved = normaliseTermSeries(series);
        return [...saved.keys()].sort((a, b) => a - b).map(y => saved.get(y)).join(' · ');
    }, [series]);

    /** The same rendering for the proposal, so the two can be read side by side. */
    const proposedSummary = useMemo(
        () => proposedYears.map(y => proposed[String(y)]).join(' · '), [proposedYears, proposed]);

    /**
     * The saved series is not the one most offers quote.
     *
     * ⚠️ NOTHING ELSE ON THIS SCREEN CATCHES THIS, AND IT IS NOT A RARE SHAPE.
     *
     * A reviewer who opens this chip before the offers finish parsing gets an empty panel with
     * one button, and "Add year" three times produces exactly 12 / 24 / 36 — the series is then
     * set, so the empty-state prompt never returns, and the panel never mentions the proposal
     * again. That is how a 10/22/34/46 tender came to be stored as 12/24/36.
     *
     * The mismatch badge could not catch it either: SERIES_TOLERANCE_MONTHS is 2, so 10 fits 12,
     * 22 fits 24 and 34 fits 36, and the only leftover — 46 — classifies as "beyond", which is
     * deliberately not badged. Every warning on this control stayed silent while every page of
     * the document was keyed to the wrong contract length.
     *
     * Stated, never applied: the majority ladder is not always the answer (a tender that asked
     * for 4/16/28/40 sits opposite offers quoting 16/28/40/52), so this says what the offers
     * quote and leaves the decision where it belongs.
     */
    const proposalDiffers = useMemo(
        () => proposedYears.length > 0 && !sameSeries(proposed, series) && !isEmpty,
        [proposedYears, proposed, series, isEmpty]);

    const setYear = (year: number, raw: string) => {
        const next: TenderTermSeries = { ...draft };
        if (raw.trim() === '') { next[String(year)] = ''; setDraft(next); return; }
        const months = parseInt(raw, 10);
        next[String(year)] = Number.isFinite(months) ? months : '';
        setDraft(next);   // committed on close, not per keystroke
    };

    const addYear = () => {
        const nextYear = years.length > 0 ? Math.max(...years) + 1 : 1;
        const last = years.length > 0 ? normalised.get(Math.max(...years)) : null;
        // Continue the tender's own step rather than guessing 12 — a 4/16/28 series steps by
        // 12, so the next entry is 40, and the reviewer only has to correct the exception.
        const step = years.length >= 2
            ? (normalised.get(years[years.length - 1])! - normalised.get(years[years.length - 2])!)
            : 12;
        commit({ ...draft, [String(nextYear)]: last ? last + step : 12 });
    };

    const removeYear = (year: number) => {
        // Renumber so the years stay a contiguous 1..N. A hole would leave a page with no term.
        const kept = years.filter(y => y !== year).map(y => normalised.get(y)!);
        const next: TenderTermSeries = {};
        kept.forEach((months, i) => { next[String(i + 1)] = months; });
        commit(next);
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 px-2.5 gap-2 text-xs font-normal ${
                        isEmpty
                            ? 'border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100'
                            : ''}`}
                    title="Which contract length each Year is, for every offer in this tender"
                >
                    <Calendar className="h-3.5 w-3.5 shrink-0" />
                    {isEmpty ? (
                        <span className="font-medium">Set contract terms</span>
                    ) : (
                        <>
                            <span className="text-muted-foreground">Contract terms</span>
                            <span className="font-mono font-medium">{summary} mo</span>
                        </>
                    )}

                    {/* The warnings live on the chip. Hiding them inside a closed popover would
                        make this control strictly worse than the block it replaced. */}
                    {duplicateMonths.length > 0 && (
                        <span className="rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-semibold text-destructive-foreground">
                            duplicate year
                        </span>
                    )}
                    {/* Only the terms that need a DECISION are badged. An offer quoting a
                        longer option than the tender asked for is not a problem, and badging it
                        made the chip cry wolf on a perfectly good offer. */}
                    {/* ⚠️ WHICH DAY THE TENDER STARTS MULTIPLIES EVERY DOLLAR ON IT.
                        Badged because it is otherwise unknowable: the window silently takes the
                        earliest date the offers quote whenever the site's expiry is blank, and
                        that is right only while the retailers agree. See the note in
                        EmailProcessingStep's parsedOfferTermSummary. */}
                    {startsDisagree && (
                        <span
                            className="rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-semibold text-destructive-foreground"
                            title={`Retailers quote different first days: ${
                                offerStarts.map(o => `${o.retailer} ${o.start}`).join(', ')}. `
                                + `Set the site's Current Contract Expiry so the tender's own start is used.`}
                        >
                            start dates differ
                        </span>
                    )}
                    {/* The offers the list below would move. Without this the chip was blank on
                        the exact tender where three retailers were about to vanish — see the note
                        on needReterm. */}
                    {needReterm.length > 0 && (
                        <span
                            className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-white"
                            title={`${needReterm.map(o => o.retailer).join(', ')} quote lengths this `
                                + `tender did not ask for. Until they are re-termed they are blank on `
                                + `every page of the document.`}
                        >
                            {needReterm.length} offer{needReterm.length > 1 ? 's' : ''} need re-terming
                        </span>
                    )}
                    {gaps.length > 0 && (
                        <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                            {gaps.reduce((n, g) => n + g.outside.length, 0)} term
                            {gaps.reduce((n, g) => n + g.outside.length, 0) > 1 ? 's' : ''} need a year
                        </span>
                    )}
                    {/* The tolerance hides a wholesale disagreement — 10/22/34/46 against a
                        12/24/36 series raises no gap at all. Badged separately, and only when
                        nothing else is already shouting. */}
                    {proposalDiffers && gaps.length === 0 && duplicateMonths.length === 0
                        && needReterm.length === 0 && (
                        <span
                            className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-white"
                            title={`Most offers here quote ${proposedSummary} months.`}
                        >
                            offers quote {proposedSummary}
                        </span>
                    )}
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                </Button>
            </PopoverTrigger>

            {/* ⚠️ ONE SCROLL REGION, AND A HEIGHT THE VIEWPORT CAN HOLD.
                The content grows with the tender — four year rows, a re-term list, and a gap
                row per offending term. On a short window that ran past the bottom of the screen
                and took the Done button with it, while the inner lists had their own little
                scrollbars, so the reviewer could scroll the middle of a dialog they could not
                scroll out of. Now: the popover is capped at the viewport, the header is pinned —
                Done and Use suggested with it, so they are reachable however long the list runs —
                and everything below scrolls as one. */}
            <PopoverContent
                align="start"
                collisionPadding={12}
                className="w-[32rem] max-w-[92vw] p-0 flex flex-col max-h-[min(80vh,42rem)]"
            >
                <div className="flex items-start justify-between gap-2 shrink-0 p-3 pb-2 border-b">
                    <div className="min-w-0">
                        <Label className="text-sm font-semibold">Contract terms for this tender</Label>
                        <p className="text-xs text-muted-foreground mt-1">
                            Which contract length each <strong>Year</strong> is, for every offer
                            here. Set once instead of choosing a year on each term of each offer.
                            An offer that quotes something different can still be overridden on
                            its own card.
                        </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        {!disabled && proposedYears.length > 0 && (
                            <Button
                                variant="ghost" size="sm" className="h-7 px-2 text-xs"
                                title={`Use the term set most of this tender's offers share: ${
                                    proposedYears.map(y => `Year ${y} = ${proposed[String(y)]}mo`).join(', ')}`}
                                onClick={() => commit(proposed)}
                            >
                                <Wand2 className="h-3 w-3 mr-1" />Use suggested
                            </Button>
                        )}
                        <Button
                            size="sm" className="h-7 px-3 text-xs"
                            title="Save the contract terms and close"
                            onClick={() => handleOpenChange(false)}
                        >
                            Done
                        </Button>
                    </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
                {startsDisagree && (
                    <div className="space-y-1.5 rounded border border-destructive bg-destructive/5 p-2 text-xs">
                        <div className="flex items-start gap-1.5 font-medium text-destructive">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <span>
                                The retailers do not agree on when supply begins. Every year below
                                is measured from{' '}
                                <span className="font-mono">{contractStart}</span> — the earliest
                                date any of them quoted.
                            </span>
                        </div>
                        <div className="pl-5 font-mono text-[11px] text-muted-foreground">
                            {offerStarts.map(o => (
                                <div key={o.retailer}>
                                    <span className="font-sans text-foreground">{o.retailer}</span>
                                    {'  '}{o.start}
                                </div>
                            ))}
                        </div>
                        <p className="pl-5 text-muted-foreground">
                            The tender really starts the day after the current contract expires.
                            Set the site's <strong>Current Contract Expiry</strong> in Step 1 and
                            that date is used instead — otherwise Year 1 is as long as the earliest
                            quote says, and every total on every retailer scales with it.
                        </p>
                    </div>
                )}
                {proposalDiffers && (
                    <div className="flex flex-wrap items-center gap-2 rounded border border-amber-400 bg-amber-50 p-2 text-xs text-amber-900">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        <span>
                            Most offers here quote{' '}
                            <span className="font-mono font-semibold">{proposedSummary}</span>
                            {' '}months. This tender is set to{' '}
                            <span className="font-mono font-semibold">{summary}</span>.
                        </span>
                        {!disabled && (
                            <Button
                                size="sm" className="h-6 px-2 text-xs ml-auto"
                                title={`Set Year 1..${proposedYears.length} to ${proposedSummary} months`}
                                onClick={() => commit(proposed)}
                            >
                                Use {proposedSummary}
                            </Button>
                        )}
                    </div>
                )}
                {years.length === 0 ? (
                    <div className="text-xs border border-dashed rounded p-3 space-y-2">
                        <p className="text-muted-foreground">
                            Not set — every term falls back to its rounded year, so two different
                            contract lengths can land on the same year and one of them takes the
                            other's place in the document.
                        </p>
                        {proposedYears.length > 0 ? (
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-muted-foreground">These offers look like:</span>
                                {proposedYears.map(y => (
                                    <span
                                        key={y}
                                        className="font-mono border rounded px-1.5 py-0.5 bg-background"
                                    >
                                        Year {y} = {proposed[String(y)]}mo
                                    </span>
                                ))}
                                {!disabled && (
                                    <Button
                                        size="sm" className="h-6 px-2 text-xs"
                                        onClick={() => commit(proposed)}
                                    >
                                        Apply
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <p className="text-muted-foreground">
                                Add the years once the offers are parsed.
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {years.map(year => {
                            const months = normalised.get(year);
                            const span = months ? spanFor(contractStart, months) : null;
                            const isDupe = months !== undefined && duplicateMonths.includes(months);
                            return (
                                <div
                                    key={year}
                                    className={`flex items-center gap-1.5 rounded border bg-background px-2 py-1.5 ${
                                        isDupe ? 'border-destructive' : ''}`}
                                >
                                    <Label className="text-xs font-semibold text-primary whitespace-nowrap">
                                        Year {year}
                                    </Label>
                                    <Input
                                        type="number"
                                        min={1}
                                        step={1}
                                        value={draft[String(year)] ?? ''}
                                        onChange={e => setYear(year, e.target.value)}
                                        disabled={disabled}
                                        className="h-7 w-16 text-sm"
                                        placeholder="mo"
                                    />
                                    <span className="text-xs text-muted-foreground">mo</span>
                                    {span && (
                                        <span
                                            className="text-[11px] text-muted-foreground font-mono border rounded px-1"
                                            title={`Ends ${iso(span.end)}. Priced over ${span.days} days (leap days excluded) = a whole-of-term multiplier of ${span.weight.toFixed(6)}.`}
                                        >
                                            {span.days} days
                                        </span>
                                    )}
                                    {!disabled && (
                                        <Button
                                            variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                                            title={`Remove Year ${year}`}
                                            onClick={() => removeYear(year)}
                                        >
                                            <X className="h-3 w-3" />
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                        {!disabled && (
                            <Button
                                variant="outline" size="sm" className="h-9 px-2 text-xs"
                                onClick={addYear}
                            >
                                <Plus className="h-3 w-3 mr-1" />Add Year
                            </Button>
                        )}
                    </div>
                )}

                {duplicateMonths.length > 0 && (
                    <div className="flex items-start gap-2 text-xs text-destructive">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <span>
                            Two years are set to{' '}
                            {duplicateMonths.map(m => `${m} months`).join(' and ')} — each year
                            needs its own contract length, or two pages will show the same data.
                        </span>
                    </div>
                )}

                {/* ⚠️ EVERY OFFER, EVERY TERM, AND NOTHING APPLIED BY ITSELF.
                    What was here before answered one question — is this ladder UNIFORMLY wrong? —
                    and showed nothing otherwise, which is exactly the case that cost money.
                    EXCH asked for 10/22/34/46; Energy Australia answered 12/24/36/46 and Shell
                    12/24/36. The 46 fits, the two-month tolerance calls 12≈10, 24≈22 and 36≈34 a
                    fit, so no plan, no gap row, no badge — while a whole-year term is deliberately
                    never re-weighted onto a 305-day Year 1, so those six terms priced over their
                    own 365-day years and read $25,093 and $31,134 against the client's $21,026 and
                    $26,180.
                    Every ladder is now listed with a suggestion per term, ticked only where
                    something would move, and every target is the reviewer's to change — including
                    to Remove, for the retailer who sent five terms against a four-year tender.
                    Flo is safe structurally rather than by refusal: a term longer than the last
                    year is suggested as Keep, never as a move onto a free Year 1. */}
                {!disabled && retermOffers.length > 0 && onApplyReterm && years.length > 0 && (
                    <div className="border-t pt-2 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium">
                                Offer terms against this series
                            </span>
                            <Button
                                size="sm" className="h-7 px-2 text-xs shrink-0"
                                /* Pressable even at zero. A disabled button states nothing, and
                                   every reason the count can be zero is invisible from here —
                                   no row ticked, every term already on its year, two lengths
                                   aimed at one year. Pressing it now says which. */
                                disabled={retermBusy}
                                title={applicable.length === 0
                                    ? 'Tick an offer whose terms would change'
                                    : `Apply the chosen years to ${applicable.length} offer(s). Rates unchanged.`}
                                onClick={async () => {
                                    setRetermBusy(true);
                                    try {
                                        await onApplyReterm(applicable.map(selectionFor));
                                    } finally { setRetermBusy(false); }
                                }}
                            >
                                <Wand2 className="h-3 w-3 mr-1" />
                                {retermBusy ? 'Applying…' : `Apply to ${applicable.length}`}
                            </Button>
                        </div>

                        <div className="space-y-1">
                            {retermOffers.map(offer => {
                                const isOpen = expanded.has(offer.offerId);
                                const clashes = clashingOffers.has(offer.offerId);
                                const rows = lengthsByOffer.get(offer.offerId) ?? [];
                                const moving = changingRows(offer);
                                // Years this offer's other lengths already hold — see yearClaims.
                                const claims = yearClaims(offer.offerId);
                                return (
                                    <div
                                        key={offer.offerId}
                                        className={`rounded border px-2 py-1.5 ${
                                            clashes ? 'border-destructive' : ''}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                className="h-3.5 w-3.5 shrink-0 accent-primary"
                                                checked={picked.has(offer.offerId)}
                                                onChange={() => setPicked(p => toggle(p, offer.offerId))}
                                                title={`Include ${offer.retailer} when Apply is pressed`}
                                            />
                                            <span className="text-xs font-medium truncate">
                                                {offer.retailer}
                                            </span>
                                            <span className="text-[11px] text-muted-foreground font-mono truncate">
                                                {rows.map(r => r.fromMonths).join(' · ')} mo
                                            </span>
                                            {moving.length > 0 ? (
                                                <span className="text-[11px] text-amber-700 font-mono truncate">
                                                    → {moving.map(r => {
                                                        const target = targetFor(offer.offerId, r);
                                                        return target.kind === 'remove'
                                                            ? `${r.fromMonths} removed`
                                                            : `${r.fromMonths}→${(target as { months: number }).months}`;
                                                    }).join('  ')}
                                                </span>
                                            ) : (
                                                <span className="text-[11px] text-muted-foreground">
                                                    nothing to change
                                                </span>
                                            )}
                                            <Button
                                                variant="ghost" size="sm"
                                                className="h-6 px-1.5 text-[11px] ml-auto shrink-0"
                                                onClick={() => setExpanded(e => toggle(e, offer.offerId))}
                                            >
                                                {isOpen ? 'Hide' : 'Change'}
                                                <ChevronDown className={`h-3 w-3 ml-0.5 transition-transform ${
                                                    isOpen ? 'rotate-180' : ''}`} />
                                            </Button>
                                        </div>

                                        {clashes && (
                                            <div className="flex items-start gap-1.5 pl-6 pt-1 text-[11px] text-destructive">
                                                <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                                                Two lengths are aimed at the same year — one of them
                                                would be dropped. Give each its own year.
                                            </div>
                                        )}

                                        {isOpen && (
                                            <div className="pl-6 pt-1.5 space-y-1">
                                                {rows.map(row => {
                                                    const target = targetFor(offer.offerId, row);
                                                    const variants = offer.terms
                                                        .filter(t => t.fromMonths === row.fromMonths).length;
                                                    return (
                                                        <div key={row.fromMonths} className="flex items-center gap-2">
                                                            <span className="text-[11px] font-mono w-14 shrink-0">
                                                                {row.fromMonths} mo
                                                            </span>
                                                            <span className="text-[11px] text-muted-foreground">→</span>
                                                            <select
                                                                className="h-6 rounded border bg-background px-1 text-[11px]"
                                                                value={targetKey(target)}
                                                                onChange={e => {
                                                                    const value = e.target.value;
                                                                    if (value === 'keep') {
                                                                        setTarget(offer.offerId, row.fromMonths, { kind: 'keep' });
                                                                    } else if (value === 'remove') {
                                                                        setTarget(offer.offerId, row.fromMonths, { kind: 'remove' });
                                                                    } else {
                                                                        const year = parseInt(value.slice(5), 10);
                                                                        const months = normalised.get(year);
                                                                        if (months !== undefined) {
                                                                            setTarget(offer.offerId, row.fromMonths,
                                                                                { kind: 'year', year, months });
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                {years.map(year => {
                                                                    const holder = claims.get(year);
                                                                    const taken = holder !== undefined
                                                                        && holder !== row.fromMonths;
                                                                    // Labelled, never blocked. Two lengths on one year
                                                                    // is refused on apply and called out in red above,
                                                                    // so the reviewer can still aim there while sorting
                                                                    // the other one out — the order people work in.
                                                                    return (
                                                                    <option key={year} value={`year:${year}`}
                                                                        title={taken
                                                                            ? `Year ${year} is already this offer's ${holder}mo term — give that one another year first`
                                                                            : undefined}>
                                                                        Year {year} · {normalised.get(year)}mo
                                                                        {taken ? ` — taken by ${holder}mo` : ''}
                                                                    </option>
                                                                    );
                                                                })}
                                                                <option value="keep">
                                                                    Keep at {row.fromMonths}mo
                                                                </option>
                                                                <option value="remove">Remove this term</option>
                                                            </select>
                                                            {/* Smooth and stepped move together — said here so the
                                                                reviewer is not hunting for a second row. */}
                                                            {variants > 1 && (
                                                                <span className="text-[11px] text-muted-foreground">
                                                                    smooth + stepped
                                                                </span>
                                                            )}
                                                            {target.kind === 'year'
                                                                && target.months === row.fromMonths && (
                                                                <span className="text-[11px] text-muted-foreground">
                                                                    already correct
                                                                </span>
                                                            )}
                                                            {/* ⚠️ SAY IT ON THE ROW, NOT ONLY IN A TOAST.
                                                                A term cannot move onto a length the offer already
                                                                quotes — AGL answered 3/15/27/39, so its 3 cannot
                                                                become the 15 that Year 1 is, because that would
                                                                replace a year of rates with three months of them.
                                                                applyOfferReterm refuses it and always did; the
                                                                reviewer just had no way to know before pressing
                                                                Apply and watching nothing happen. */}
                                                            {target.kind === 'year'
                                                                && target.months !== row.fromMonths
                                                                && rows.some(r => r.fromMonths === target.months) && (
                                                                <span className="text-[11px] text-destructive">
                                                                    can't — this offer already quotes {target.months}mo
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <p className="text-xs text-muted-foreground">
                            Rates are never changed — only each term's length and its date range, so
                            the retailer's own rate is billed over this tender's period. Nothing is
                            saved until you approve the offer.
                        </p>
                    </div>
                )}

                {/* A GAP: the retailer answered with a length between two of the tender's
                    years, so that year of their offer is missing from the comparison until
                    someone decides which year it was. No rule can decide it — 24 months is
                    equidistant from a 16-month Year 2 and a 28-month Year 3 — so both choices
                    are offered and neither is taken automatically. */}
                {gaps.length > 0 && (
                    <div className="space-y-1.5 border-t pt-2">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {gaps.length} offer{gaps.length > 1 ? 's' : ''} answered with a length
                            between this tender's years
                        </div>
                        {gaps.map(m => (
                            <div key={`gap-${m.retailer}`} className="text-xs pl-5 space-y-1">
                                {m.outside.map(({ months, placement }) => (
                                    <div key={months} className="flex flex-wrap items-center gap-1.5">
                                        <span className="text-muted-foreground">
                                            <strong className="text-foreground">{m.retailer}</strong>{' '}
                                            quotes <strong>{months}mo</strong>
                                            {placement?.kind === 'between' && (
                                                <> — between Year {placement.lowerYear} ({placement.lowerMonths}mo)
                                                    and Year {placement.upperYear} ({placement.upperMonths}mo)</>
                                            )}
                                            {placement?.kind === 'before' && (
                                                <> — shorter than Year {placement.firstYear} ({placement.firstMonths}mo)</>
                                            )}
                                            . Make it:
                                        </span>
                                        {/* ⚠️ ONLY THE YEARS THIS OFFER HAS LEFT.
                                            Engie answered 3 / 15 / 27 / 39 against a 15/27/39/51
                                            series, so Years 1-3 are already its own longer terms.
                                            Offering "Make it Year 1 · 15mo" there asks one year to
                                            hold two terms, which movesFromTargets refuses — the
                                            button looked live and did nothing. A length with no
                                            year left to take is kept or removed, and the line
                                            below says so instead of offering a dead button. */}
                                        {!disabled && onRetermTerm && m.offerId && placement
                                            && (placement.kind === 'between'
                                                ? [
                                                    { year: placement.lowerYear, months: placement.lowerMonths },
                                                    { year: placement.upperYear, months: placement.upperMonths },
                                                  ]
                                                : placement.kind === 'before'
                                                ? [{ year: placement.firstYear, months: placement.firstMonths }]
                                                // 'beyond' is filtered out of `gaps` — it belongs
                                                // in the extras list below, with no action.
                                                : []
                                            ).filter(choice => {
                                                const holder = yearClaims(m.offerId!).get(choice.year);
                                                return holder === undefined || holder === months;
                                            }).map(choice => (
                                                <Button
                                                    key={choice.year}
                                                    variant="outline" size="sm"
                                                    className="h-6 px-2 text-[11px]"
                                                    title={`Re-term this offer's ${months}-month term to ${choice.months} months, so it is priced as Year ${choice.year}. Rates unchanged.`}
                                                    onClick={() => onRetermTerm(m.offerId!, months, choice.months)}
                                                >
                                                    Year {choice.year} · {choice.months}mo
                                                </Button>
                                            ))}
                                        {m.offerId && placement && placement.kind !== 'beyond'
                                            && (placement.kind === 'between'
                                                ? [placement.lowerYear, placement.upperYear]
                                                : [placement.firstYear]
                                            ).every(yr => {
                                                const holder = yearClaims(m.offerId!).get(yr);
                                                return holder !== undefined && holder !== months;
                                            }) && (
                                            <span className="text-[11px] text-muted-foreground">
                                                — this offer's own longer terms already hold those
                                                years, so keep or remove it.
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                        <p className="text-xs text-muted-foreground pl-5">
                            Until one is chosen that year of the offer does not appear in the
                            document. Rates are never changed — only the term's length and dates.
                        </p>
                    </div>
                )}

                {/* AN EXTRA OPTION, NOT A PROBLEM. A retailer quoting a longer term than the
                    tender asked for has answered correctly and then offered more. Flo's 52-month
                    option on a four-year tender is the case: its 16/28/40 already sit on Years
                    2/3/4 (it has no 4-month quote, so it starts at Year 2 — which is also how the
                    client's own sheet reads, leaving Flo's Year 1 blank). Stated plainly and
                    without a warning colour, because there is nothing to do unless the reviewer
                    decides the tender really wanted that length. */}
                {extras.length > 0 && (
                    <div className="space-y-1 border-t pt-2 text-xs text-muted-foreground">
                        {extras.map(m => (
                            <div key={`extra-${m.retailer}`}>
                                <strong className="text-foreground">{m.retailer}</strong> also
                                quotes {m.outside.map(o => `${o.months}mo`).join(', ')} — longer
                                than Year {seriesYears(series).length} and outside this tender, so
                                {m.outside.length > 1 ? ' they will' : ' it will'} not appear.
                                Add a Year for {m.outside.length > 1 ? 'them' : 'it'} only if the
                                tender did ask for that length.
                            </div>
                        ))}
                    </div>
                )}

                </div>
            </PopoverContent>
        </Popover>
    );
};
